import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@akuna/ui";
import { CustomIcons } from "@/components/icons/CustomIcons";
import type { PrintZone } from "@/components/print/DocumentPreview";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePrintSettings, useUpdatePrintSettings } from "@/hooks/usePrintSettings";
import { buildSamplePreviewHtml, type PreviewVariant } from "@/lib/print/previewHtml";
import { zoneForFirstError } from "./fieldZone";
import { MobilePrintLayoutEditor } from "./MobilePrintLayoutEditor";
import { AreaNav } from "./AreaNav";
import { AreaPanel } from "./panels/AreaPanel";
import { PreviewCard } from "./PreviewCard";
import {
  formValuesFromSettings,
  previewSettingsFromForm,
  printLayoutFormSchema,
  settingsPatchFromForm,
  type PrintLayoutFormValues,
  type PrintScope,
} from "./printLayoutForm";
import { useImageUpload } from "./useImageUpload";

export function PrintLayoutEditor({ scope }: { scope: PrintScope }) {
  const { t, i18n } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");
  const { data: settings } = usePrintSettings();
  const update = useUpdatePrintSettings();
  const form = useForm<PrintLayoutFormValues>({
    resolver: zodResolver(printLayoutFormSchema),
    defaultValues: formValuesFromSettings(settings, scope),
  });
  const isMobile = useIsMobile();
  // useIsMobile settles in an effect; wait one commit so desktop never flashes on phones.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const images = useImageUpload();
  const [zone, setZone] = useState<PrintZone>("page");
  const [variant, setVariant] = useState<PreviewVariant>("simples");
  const { reset: resetImages } = images;

  useEffect(() => {
    form.reset(formValuesFromSettings(settings, scope));
    resetImages();
  }, [settings, scope, form, resetImages]);

  useEffect(() => setZone("page"), [scope]);

  const values = form.watch();
  const lang = i18n.language?.startsWith("en") ? "en" : "pt-BR";
  const html = useMemo(
    () =>
      buildSamplePreviewHtml({
        printSettings: previewSettingsFromForm(values, scope, images.previews),
        scope,
        variant,
        lang,
        zones: true,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(values), scope, variant, images.previews, lang],
  );

  const onInvalid = (errors: Parameters<typeof zoneForFirstError>[0]) => {
    const target = zoneForFirstError(errors);
    if (target) setZone(target);
    toast.error(t("printEditor.fixErrors"));
  };

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      const uploaded = await images.uploadPending();
      const final: PrintLayoutFormValues = {
        ...v,
        backgroundImageUrl: uploaded.background ?? v.backgroundImageUrl,
        headerImageUrl: uploaded.header ?? v.headerImageUrl,
      };
      await update.mutateAsync({ settings: settingsPatchFromForm(final, scope), suppressSuccessToast: true, suppressErrorToast: true });
      toast.success(t("settingsSaved"));
    } catch (error: unknown) {
      console.error("Erro ao salvar configurações:", error);
      toast.error(error instanceof Error ? error.message : t("saveError"));
    }
  }, onInvalid);

  const cancel = () => {
    form.reset(formValuesFromSettings(settings, scope));
    images.reset();
  };

  const isSaving = form.formState.isSubmitting;

  const actions = (
    <>
      <Button type="button" variant="outline" className="min-h-11 shadow-soft-search sm:min-h-0" onClick={cancel} disabled={isSaving}>
        <CustomIcons name="cancel-circle" className="!h-6 !w-6 !text-color-dark" />
        {tCommon("cancel")}
      </Button>
      <Button type="submit" className="min-h-11 w-full sm:min-h-0 sm:w-auto" disabled={isSaving}>
        <CustomIcons name="check-circle" className="!h-6 !w-6 !text-primary-foreground" />
        <span className="font-normal">{isSaving ? tCommon("loading") : tCommon("save")}</span>
      </Button>
    </>
  );

  if (!mounted) return null;

  if (isMobile) {
    return (
      <FormProvider {...form}>
        <form onSubmit={onSubmit} noValidate>
          <MobilePrintLayoutEditor
            zone={zone}
            onZoneChange={setZone}
            scope={scope}
            images={images}
            onPreviewDigital={() => setVariant("digital")}
            actions={actions}
            preview={
              <PreviewCard
                compact
                html={html}
                scope={scope}
                variant={variant}
                onVariantChange={setVariant}
                activeZone={zone}
                onZoneClick={setZone}
              />
            }
          />
        </form>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={onSubmit} className="space-y-6" noValidate>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <section
            aria-label={t("printEditor.propertiesAria")}
            className="w-full overflow-hidden rounded-card border border-border/60 bg-card text-card-foreground shadow-soft-card lg:w-[360px] lg:shrink-0"
          >
            <div className="p-5 pb-1">
              <h2 className="text-lg font-semibold">
                {t(scope === "term" ? "printEditor.titleTerm" : "printEditor.titlePrescription")}
              </h2>
              <p className="pt-1 text-[13px] leading-snug text-muted-foreground">{t("printEditor.subtitle")}</p>
            </div>
            <AreaNav value={zone} onChange={setZone} />
            <AreaPanel zone={zone} images={images} onPreviewDigital={() => setVariant("digital")} scope={scope} />
          </section>
          <PreviewCard
            html={html}
            scope={scope}
            variant={variant}
            onVariantChange={setVariant}
            activeZone={zone}
            onZoneClick={setZone}
          />
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">{actions}</div>
      </form>
    </FormProvider>
  );
}
